<script setup lang="ts">
    import { resolveStoryblokLink } from '~/utils/resolveStoryblokLink';

    const props = defineProps<{ config: ConfigStoryblok }>();

    defineEmits<{
        click: [void];
    }>();

    const countryLanguages = useCountryLanguages();
    const { lang } = useLanguage();

    const resolvedLinks = computed(() => {
        return props.config.metaNavigationLinks
            ?.map((link) => {
                // Prevent crashing the entire meta navigation if a single link is invalid
                try {
                    return {
                        ...link,
                        to: resolveStoryblokLink(link.link, countryLanguages, lang.value)
                    };
                } catch (error) {
                    console.warn(error);
                    return null;
                }
            })
            .filter(Boolean) as (LinkStoryblok & { to: string })[] | null;
    });
</script>

<template>
    <nav class="container">
        <ul class="links">
            <li
                v-for="link in resolvedLinks || []"
                :key="link._uid"
            >
                <NuxtLink
                    :to="link.to"
                    :target="link.link.target"
                    class="link"
                    @click="$emit('click')"
                >
                    {{ link.displayName }}
                </NuxtLink>
            </li>
        </ul>
    </nav>
</template>

<style lang="scss" scoped>
    .container {
        background-color: var(--color-theme-white);
        padding-top: var(--spacing);
        padding-bottom: var(--spacing);

        @media (min-width: $br-l) {
            margin-left: auto;
            background-color: transparent;
            padding-top: 0;
            padding-bottom: 0;
        }
    }

    .links {
        @media (min-width: $br-l) {
            display: flex;
            gap: var(--spacing);
            height: 100%;
        }
    }

    .link {
        display: block;
        padding: var(--spacing) var(--spacing--2);
        font-weight: 300;
        font-size: var(--font-xs);
        position: relative;

        @media (min-width: $br-l) {
            display: grid;
            place-content: center;
            height: 100%;
            padding: 0;
        }

        &::after {
            content: '';
            position: absolute;
            top: calc(50% + 1rem);
            left: 0;
            z-index: -1;
            height: 2px;
            width: 100%;
            background-color: var(--header-hover-color, $header-hover-color);
            border-radius: 100rem;
            transform: scaleX(0);
            transform-origin: left;
            transition: transform $anim-duration-hover $anim-ease;
        }

        &:hover,
        &:focus-visible {
            &::after {
                transform: scaleX(1.05);
            }
        }
    }
</style>
